import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Form, Formik } from 'formik'
import { Box, Button, Card, Checkbox, Grid, H6 } from '@dhl-ui/components'

import { dateConversions, KeyboardDatePickerInput } from '@dhl/inputs'
import { TextFieldInput } from '@dhl/formik'

import {
  GetReportFinanceShipmentsParams,
  useGetReportFinanceDownloadpaymentsHook,
  useGetReportFinanceShipmentsHook,
} from '@dhl/mdp-api'

import { downloadReport, useDataAsyncCall } from '~/api/admin-api'
import { useNotificationMessage } from '~/common'
import { createStartDateEndDateValidationSchema } from '../model/validation'

type Countries = {
  countries: string[]
}

export type FinanceFormValues = {
  interval: {
    from: Date | null
    till: Date | null
  }
  countries: Record<string, boolean>
  billingEntities: Record<string, boolean>
  invoiceNumber: string
  otcOrder: boolean
  reportType?: boolean
}

export const FinanceReportPage = () => {
  const { t } = useTranslation()
  const { showError } = useNotificationMessage()
  const getData1 = useGetReportFinanceShipmentsHook()
  const getData2 = useGetReportFinanceDownloadpaymentsHook()

  const [countries] = useDataAsyncCall<Countries>(`admin-user/possible-countries`)
  const [billingEntities] = useDataAsyncCall<string[]>(
    `report/finance/billingentities?countries=${
      countries?.countries === undefined ? '' : countries?.countries.join(',')
    }`
  )

  const onSubmit = async (values: FinanceFormValues) => {
    const params: GetReportFinanceShipmentsParams = {
      startDate: dayjs(values.interval.from).format('YYYY-MM-DD').toString(),
      endDate: dayjs(values.interval.till).format('YYYY-MM-DD').toString(),
      countries: Object.keys(values.countries)
        .filter((country) => values.countries[country])
        .join(','),
      billingEntities: Object.keys(values.billingEntities)
        .filter((billingEntity) => values.billingEntities[billingEntity])
        .join(','),
      invoiceNrs: values.invoiceNumber,
      otcOnly: values.otcOrder,
    }

    const response = values.reportType
      ? await downloadReport(getData1(params), 'financeData.csv')
      : await downloadReport(getData2(params), 'paymentData.csv')

    if (response) {
      showError(JSON.parse(response).message)
    }
  }

  const validationSchema = createStartDateEndDateValidationSchema(t)

  const initialValues: FinanceFormValues = useMemo(
    () => ({
      interval: {
        from: null,
        till: null,
      },
      countries: Object.assign({}, ...(countries?.countries || []).map((country: string) => ({ [country]: true }))),
      billingEntities: Object.assign(
        {},
        ...(billingEntities || []).map((billingEntity: string) => ({ [billingEntity]: true }))
      ),
      invoiceNumber: '',
      otcOrder: false,
    }),
    [countries, billingEntities]
  )

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnChange={false}
      enableReinitialize
    >
      {({ isSubmitting, values, setFieldValue, handleSubmit }) => (
        <Card>
          <Card.Header title={t('admin.reportsPage.financeData')} />
          <Card.Content>
            <Form data-testid="finance-form">
              <Grid container spacing={2} direction="row" alignItems="center">
                <Grid size={{ xs: 2 }}>
                  <H6>{t('admin.reportsPage.dateRange')}</H6>
                </Grid>
                <Grid size={{ xs: 10 }} container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <KeyboardDatePickerInput
                      variant="standard"
                      label={t('admin.reportsPage.startDate')}
                      name="interval.from"
                      conversions={dateConversions}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <KeyboardDatePickerInput
                      variant="standard"
                      label={t('admin.reportsPage.endDate')}
                      name="interval.till"
                      conversions={dateConversions}
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <Grid size={{ xs: 2 }}>
                  <H6>{t('admin.reportsPage.country')}</H6>
                </Grid>
                <Grid size={{ xs: 10 }} gap={1} display="grid" gridTemplateColumns="auto auto auto auto auto">
                  {Object.keys(values.countries).map((country) => (
                    <Checkbox
                      name={`countries.${country}`}
                      key={country}
                      label={t(`inputs.destinationInput.country_${country}`)}
                    />
                  ))}
                </Grid>
                <Grid size={{ xs: 2 }}>
                  <H6>{t('admin.reportsPage.billingEntry')}</H6>
                </Grid>
                <Grid size={{ xs: 10 }} display="grid" gridTemplateColumns="auto auto auto auto auto auto auto">
                  {Object.keys(values.billingEntities).map((billingentity) => (
                    <Box key={billingentity} sx={{ mr: 1, mb: 1 }}>
                      <Checkbox name={`billingEntities.${billingentity}`} label={billingentity} />
                    </Box>
                  ))}
                </Grid>
                <Grid size={{ xs: 2 }}>
                  <H6>{t('admin.reportsPage.invoiceNumber')}</H6>
                </Grid>

                <Grid size={{ xs: 10 }}>
                  <TextFieldInput
                    fullWidth
                    label={t('admin.reportsPage.invoiceNumber')}
                    name="invoiceNumber"
                    type="number"
                  />
                </Grid>

                <Grid size={{ xs: 2 }}>
                  <H6>{t('admin.reportsPage.otcOrder')}</H6>
                </Grid>
                <Grid size={{ xs: 10 }}>
                  <Checkbox name="otcOrder" label={t('admin.reportsPage.otcOrder')} />
                </Grid>
                <Grid size={{ xs: 12 }} display="flex" justifyContent="flex-end" gap={1}>
                  <Button
                    disabled={isSubmitting}
                    onClick={() => {
                      setFieldValue('reportType', true).then(() => {})
                      handleSubmit()
                    }}
                  >
                    {t('admin.reportsPage.downloadFinance')}
                  </Button>
                  <Button
                    disabled={isSubmitting}
                    onClick={() => {
                      setFieldValue('reportType', false).then(() => {})
                      handleSubmit()
                    }}
                  >
                    {t('admin.reportsPage.downloadPayments')}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          </Card.Content>
        </Card>
      )}
    </Formik>
  )
}
